import React, { useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Anchor, Check, Upload, Trash2, Image as ImageIcon, Loader2, AlertTriangle, Palette, Lock, FileText as FileIcon, Eye, Mail, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import InvoiceEmailPreview from "@/components/InvoiceEmailPreview";
import ApiIntegrationsSection from "@/components/ApiIntegrationsSection";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const JURISDICTIONS = [
    { code: "KY", name: "Cayman Islands", noTax: true },
    { code: "US", name: "United States", noTax: false },
    { code: "GB", name: "United Kingdom", noTax: false },
    { code: "CA", name: "Canada", noTax: false },
    { code: "BS", name: "Bahamas", noTax: true },
    { code: "BB", name: "Barbados", noTax: false },
    { code: "AU", name: "Australia", noTax: false },
    { code: "OTHER", name: "Other", noTax: false },
];

export default function Settings() {
    const { currentOrg, setCurrentOrg, refreshOrgs, memberships } = useAuth();
    const [form, setForm] = useState(() => ({ ...currentOrg }));
    const [busy, setBusy] = useState(false);
    const [logoBusy, setLogoBusy] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState("");
    const [deleteBusy, setDeleteBusy] = useState(false);
    const logoRef = useRef(null);
    const navigate = useNavigate();

    const currentMembership = (memberships || []).find((m) => m.org_id === currentOrg?.id);
    const isOwner = currentMembership?.role === "Owner";

    const handleDelete = async () => {
        if (deleteConfirm.trim() !== (currentOrg.name || "").trim()) {
            toast.error("The name you typed does not match");
            return;
        }
        setDeleteBusy(true);
        try {
            const { data } = await api.delete(`/orgs/${currentOrg.id}`, {
                data: { confirm_name: deleteConfirm.trim() },
            });
            toast.success(`"${data.org_name}" deleted`);
            // Clear active org, reload orgs, and redirect to onboarding or another org
            localStorage.removeItem("bl_org_id");
            await refreshOrgs();
            setDeleteOpen(false);
            navigate("/app", { replace: true });
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDeleteBusy(false); }
    };

    React.useEffect(() => { if (currentOrg) setForm({ ...currentOrg }); }, [currentOrg]);
    if (!currentOrg) return null;

    const save = async (patch = {}) => {
        setBusy(true);
        try {
            const body = { ...patch };
            const { data } = await api.patch(`/orgs/${currentOrg.id}`, body);
            setCurrentOrg(data);
            await refreshOrgs();
            toast.success("Settings saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const uploadLogo = async (file) => {
        if (!file) return;
        setLogoBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const orgId = localStorage.getItem("bl_org_id");
            const { data } = await axios.post(`${API_BASE}/orgs/${currentOrg.id}/logo`, fd, {
                withCredentials: true,
                headers: { "X-Org-Id": orgId || "" },
            });
            setForm((f) => ({ ...f, logo_url: data.logo_url }));
            setCurrentOrg({ ...currentOrg, logo_url: data.logo_url });
            await refreshOrgs();
            toast.success("Logo uploaded");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLogoBusy(false); if (logoRef.current) logoRef.current.value = ""; }
    };

    const removeLogo = async () => {
        setLogoBusy(true);
        try {
            await api.delete(`/orgs/${currentOrg.id}/logo`);
            setForm((f) => ({ ...f, logo_url: null }));
            setCurrentOrg({ ...currentOrg, logo_url: null });
            await refreshOrgs();
            toast.success("Logo removed");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLogoBusy(false); }
    };

    const isKY = form.jurisdiction === "KY";

    return (
        <div>
            <PageHeader subtitle="Workspace" title="Settings" />
            <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
                <section className="rounded-3xl bg-white border border-sand-200 shadow-card p-6">
                    <h3 className="font-heading text-xl text-ocean-700">Organization</h3>
                    <div className="mt-5 space-y-4">
                        <div><Label>Business name</Label><Input className="mt-1.5" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="org-name-input" /></div>
                        <div><Label>Address</Label><Input className="mt-1.5" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                        <div>
                            <Label htmlFor="contact-email">Contact email</Label>
                            <Input
                                id="contact-email"
                                type="email"
                                className="mt-1.5"
                                value={form.contact_email || ""}
                                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                                placeholder="billing@yourcompany.com"
                                data-testid="org-contact-email-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                                Customer replies to your invoice, receipt, and statement emails will be routed here.
                                Appears as the <code className="font-mono text-[11px]">Reply-To</code> on every outbound email; your business name appears as the sender.
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Industry</Label><Input className="mt-1.5" value={form.industry || ""} onChange={(e) => setForm({ ...form, industry: e.target.value })} /></div>
                            <div><Label>Currency</Label><Input className="mt-1.5" value={form.currency || ""} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
                        </div>
                        <Button onClick={() => save({ name: form.name, address: form.address, contact_email: form.contact_email || null, industry: form.industry, currency: form.currency })} disabled={busy} className="bg-ocean-700 text-white rounded-xl" data-testid="org-save-btn">Save organization</Button>
                    </div>
                </section>

                <section className="rounded-3xl bg-white border border-sand-200 shadow-card p-6">
                    <div className="flex items-center gap-2">
                        <Anchor className="h-5 w-5 text-coral" />
                        <h3 className="font-heading text-xl text-ocean-700">Tax & jurisdiction</h3>
                    </div>
                    <div className="mt-5 space-y-4">
                        <div>
                            <Label>Jurisdiction</Label>
                            <Select value={form.jurisdiction} onValueChange={(v) => setForm({ ...form, jurisdiction: v, cayman_no_tax_mode: v === "KY" })}>
                                <SelectTrigger className="mt-1.5" data-testid="jurisdiction-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{JURISDICTIONS.map((j) => <SelectItem key={j.code} value={j.code}>{j.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>

                        <div className="rounded-2xl border border-coral/30 bg-coral/5 p-4 flex gap-3">
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <div className="text-sm font-medium text-ocean-700">Cayman no-tax mode</div>
                                    {isKY && <Badge className="bg-coral/15 text-coral-dark border-0">Recommended</Badge>}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                    {isKY
                                        ? "This Cayman Islands company is configured as not subject to standard corporate income tax. Invoices and reports default to zero tax."
                                        : "Not applicable to the selected jurisdiction. Configure a tax engine below."}
                                </p>
                            </div>
                            <Switch
                                checked={!!form.cayman_no_tax_mode}
                                disabled={!isKY}
                                onCheckedChange={(v) => setForm({ ...form, cayman_no_tax_mode: v, tax_mode: v ? "none" : form.tax_mode })}
                                data-testid="cayman-tax-toggle"
                            />
                        </div>

                        <div>
                            <Label>Tax engine</Label>
                            <Select value={form.tax_mode} onValueChange={(v) => setForm({ ...form, tax_mode: v })}>
                                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">No tax</SelectItem>
                                    <SelectItem value="vat">VAT</SelectItem>
                                    <SelectItem value="sales">Sales tax</SelectItem>
                                    <SelectItem value="custom">Custom / mixed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {form.tax_mode !== "none" && (
                            <div>
                                <Label>Default tax rate (%)</Label>
                                <Input className="mt-1.5" type="number" value={form.default_tax_rate || 0} onChange={(e) => setForm({ ...form, default_tax_rate: Number(e.target.value) })} />
                            </div>
                        )}
                        <Button onClick={() => save({ jurisdiction: form.jurisdiction, cayman_no_tax_mode: form.cayman_no_tax_mode, tax_mode: form.tax_mode, default_tax_rate: form.default_tax_rate })} disabled={busy} className="bg-ocean-700 text-white rounded-xl" data-testid="save-tax-settings">
                            Save tax settings
                        </Button>
                    </div>
                </section>

                <section className="rounded-3xl bg-white border border-sand-200 shadow-card p-6 lg:col-span-2">
                    <h3 className="font-heading text-xl text-ocean-700">Branding</h3>
                    <p className="text-xs text-muted-foreground mt-1">Show your brand on invoices, the sidebar, and the customer portal.</p>
                    <div className="mt-6 grid sm:grid-cols-[auto,1fr] gap-8 items-start">
                        <div>
                            <Label className="mb-2 block">Organization logo</Label>
                            <div className="h-28 w-28 rounded-2xl border border-dashed border-sand-300 bg-sand-50 grid place-items-center overflow-hidden" data-testid="logo-preview">
                                {form.logo_url ? (
                                    <img src={form.logo_url} alt="logo" className="h-full w-full object-contain" />
                                ) : (
                                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                                )}
                            </div>
                            <div className="mt-3 flex gap-2">
                                <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="logo-file-input" />
                                <Button variant="outline" size="sm" className="rounded-lg border-sand-300" onClick={() => logoRef.current?.click()} disabled={logoBusy} data-testid="upload-logo-btn">
                                    {logoBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />} Upload
                                </Button>
                                {form.logo_url && (
                                    <Button variant="ghost" size="sm" className="text-coral-dark" onClick={removeLogo} disabled={logoBusy} data-testid="remove-logo-btn">
                                        <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                                    </Button>
                                )}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-2 max-w-xs">PNG, JPG or WEBP · Max 1MB · Square recommended</p>
                        </div>
                        <div>
                            <Label>Accent color</Label>
                            <Input type="color" className="mt-1.5 h-11 w-28" value={form.branding_color || "#0B2B40"} onChange={(e) => setForm({ ...form, branding_color: e.target.value })} />
                            <p className="text-xs text-muted-foreground mt-2">Used on invoices and customer-facing pages.</p>
                            <Button onClick={() => save({ branding_color: form.branding_color })} disabled={busy} className="bg-ocean-700 text-white rounded-xl mt-4">Save color</Button>
                        </div>
                    </div>
                </section>

                <InvoiceDefaultsSection />

                <EmailMessagesSection />

                <BrandingWhiteLabelSection />

                <ApiIntegrationsSection />

                {/* Handy docs link — visible to everyone, opens the printable manual in a new tab. */}
                <section className="rounded-3xl bg-white border border-sand-200 shadow-card p-5 lg:col-span-2 flex items-center gap-4 flex-wrap" data-testid="settings-manual-link">
                    <div className="h-10 w-10 rounded-xl bg-secondary/10 text-secondary-dark grid place-items-center shrink-0">
                        <BookOpen className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-[240px]">
                        <div className="font-heading text-base text-ocean-700">User manual</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            Every feature explained, ready to print or save as PDF.
                        </div>
                    </div>
                    <a href="/manual" target="_blank" rel="noopener noreferrer" className="text-sm text-secondary-dark hover:text-secondary inline-flex items-center gap-1">
                        Open manual →
                    </a>
                    <a href="/BlueLedger-User-Manual.md" download className="text-sm text-secondary-dark hover:text-secondary inline-flex items-center gap-1">
                        Download .md
                    </a>
                </section>

                {isOwner && (
                    <section className="rounded-3xl bg-white border-2 border-coral/30 shadow-card p-6 lg:col-span-2">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-coral" />
                            <h3 className="font-heading text-xl text-ocean-700">Danger zone</h3>
                        </div>
                        <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex-1">
                                <div className="font-medium text-ocean-700">Delete this organization</div>
                                <p className="text-xs text-muted-foreground mt-1 leading-relaxed max-w-xl">
                                    Permanently remove <span className="font-semibold">{currentOrg.name}</span> along with
                                    every invoice, bill, expense, customer, vendor, item, payment, bank account, journal entry,
                                    receipt and audit log associated with it. Teammates will lose access immediately.
                                    This cannot be undone.
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                onClick={() => { setDeleteConfirm(""); setDeleteOpen(true); }}
                                className="border-coral text-coral-dark hover:bg-coral/5 rounded-xl shrink-0"
                                data-testid="open-delete-org"
                            >
                                <Trash2 className="h-4 w-4 mr-1.5" /> Delete organization
                            </Button>
                        </div>
                    </section>
                )}
            </div>

            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="text-coral-dark flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5" /> Delete {currentOrg?.name}?
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="text-sm text-ocean-700">
                            This will permanently delete the organization and every record tied to it.
                            This action <span className="font-semibold">cannot be undone</span>.
                        </div>
                        <div className="rounded-xl bg-coral/5 border border-coral/30 p-3 text-xs text-coral-dark">
                            To confirm, type the exact organization name below:
                            <div className="mt-1.5 font-mono text-[13px] text-ocean-700 select-all">{currentOrg?.name}</div>
                        </div>
                        <Input
                            value={deleteConfirm}
                            onChange={(e) => setDeleteConfirm(e.target.value)}
                            placeholder="Type the organization name"
                            data-testid="delete-org-confirm-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleteBusy}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDelete}
                            disabled={deleteBusy || deleteConfirm.trim() !== (currentOrg?.name || "").trim()}
                            className="bg-coral hover:bg-coral-dark text-white"
                            data-testid="confirm-delete-org-btn"
                        >
                            {deleteBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                            Delete permanently
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function BrandingWhiteLabelSection() {
    const { currentOrg, features, refreshOrgs } = useAuth();
    const whiteLabel = !!features?.white_label;
    const branding = currentOrg?.branding || {};
    const [hide, setHide] = useState(!!branding.hide_blueledger_footer);
    const [tagline, setTagline] = useState(branding.footer_tagline || "");
    const [busy, setBusy] = useState(false);

    const save = async () => {
        if (!whiteLabel) return;
        setBusy(true);
        try {
            await api.patch(`/orgs/${currentOrg.id}/branding`, {
                hide_blueledger_footer: hide,
                footer_tagline: tagline || null,
            });
            await refreshOrgs();
            toast.success("White-label settings saved");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    return (
        <section className="rounded-3xl bg-white border border-sand-200 shadow-card p-6 lg:col-span-2">
            <div className="flex items-center gap-2">
                <Palette className="h-5 w-5 text-secondary-dark" />
                <h3 className="font-heading text-xl text-ocean-700">White-labeling</h3>
                {whiteLabel ? (
                    <Badge className="bg-secondary/10 text-secondary-dark border-0">Team plan</Badge>
                ) : (
                    <Badge className="bg-sand-200 text-ocean-700 border-0">Team plan only</Badge>
                )}
            </div>

            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                Every invoice, estimate, and payment receipt that BlueLedger sends out includes a small
                <strong className="text-ocean-700"> &ldquo;Made with BlueLedger&rdquo;</strong> link in the footer.
                On the Team plan you can hide it, replace it with your own tagline, or both.
            </p>

            <div className={`mt-5 space-y-4 ${whiteLabel ? "" : "opacity-60 pointer-events-none"}`}>
                <label className="flex items-start gap-3 rounded-2xl border border-sand-300 p-4 cursor-pointer">
                    <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 accent-secondary"
                        checked={hide}
                        onChange={(e) => setHide(e.target.checked)}
                        data-testid="white-label-hide-toggle"
                        disabled={!whiteLabel}
                    />
                    <div className="text-sm">
                        <div className="font-medium text-ocean-700">Hide the &ldquo;Made with BlueLedger&rdquo; footer</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            Outbound customer emails (invoices, estimates, receipts, dunning, statements) will not display the BlueLedger brand link.
                        </div>
                    </div>
                </label>

                <div>
                    <Label>Custom footer tagline (optional)</Label>
                    <Input
                        className="mt-1.5"
                        value={tagline}
                        onChange={(e) => setTagline(e.target.value)}
                        placeholder={`e.g. "${currentOrg?.name || "Your company"} · Questions? Reply to this email."`}
                        disabled={!whiteLabel}
                        data-testid="white-label-tagline"
                    />
                    <p className="text-xs text-muted-foreground mt-1.5">
                        {hide
                            ? "Shown in place of the BlueLedger footer. Leave blank for a clean, unbranded email."
                            : "Appears centered, just below the BlueLedger footer link."}
                    </p>
                </div>

                <Button
                    onClick={save}
                    disabled={busy || !whiteLabel}
                    className="bg-ocean-700 text-white rounded-xl"
                    data-testid="white-label-save"
                >
                    {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                    Save white-label settings
                </Button>
            </div>

            {!whiteLabel && (
                <div className="mt-5 inline-flex items-center gap-2 rounded-xl bg-sand-100 text-ocean-700 text-sm px-3 py-2">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <Link to="/pricing" className="hover:underline">Upgrade to Team</Link>
                    <span className="text-muted-foreground">to unlock white-labeling.</span>
                </div>
            )}
        </section>
    );
}


const INVOICE_TEMPLATES = [
    {
        id: "classic",
        name: "Classic",
        tagline: "Clean, ocean-blue cards. Familiar and friendly.",
        accent: "#00A3B5",
    },
    {
        id: "modern",
        name: "Modern",
        tagline: "Bold dark header with a high-contrast total. Stands out in a busy inbox.",
        accent: "#0B2B40",
    },
    {
        id: "minimal",
        name: "Minimal",
        tagline: "Monochrome, serif, print-ready. Great for professional services.",
        accent: "#5b7085",
    },
];

function EmailMsgField({ label, value, onChange, defaultText, testId }) {
    return (
        <div>
            <Label>{label}</Label>
            <Textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={3}
                className="mt-1.5"
                placeholder={defaultText}
                data-testid={testId}
            />
            <p className="text-[11px] text-muted-foreground mt-1">
                {value.trim()
                    ? "Custom text will replace the default on outgoing emails."
                    : "Leave blank to use the default copy shown above."}
            </p>
        </div>
    );
}


function EmailMessagesSection() {
    const { currentOrg, refreshOrgs } = useAuth();
    const stored = currentOrg?.email_messages || {};
    const [invoice, setInvoice] = useState(stored.invoice ?? "");
    const [estimate, setEstimate] = useState(stored.estimate ?? "");
    const [receipt, setReceipt] = useState(stored.receipt ?? "");
    const [statement, setStatement] = useState(stored.statement ?? "");
    const [busy, setBusy] = useState(false);

    React.useEffect(() => {
        const m = currentOrg?.email_messages || {};
        setInvoice(m.invoice ?? "");
        setEstimate(m.estimate ?? "");
        setReceipt(m.receipt ?? "");
        setStatement(m.statement ?? "");
    }, [currentOrg]);

    const DEFAULTS = {
        invoice: "Questions? Just reply to this email and someone from our team will get right back to you.",
        estimate: "Questions? Just reply to this email and someone from our team will get right back to you.",
        receipt: "If anything looks wrong, just reply to this email and we'll take care of it.",
        statement: "Questions? Just reply to this email and a real human will get back to you.",
    };

    const save = async () => {
        setBusy(true);
        try {
            // Trim and treat blanks as "no override" (sent as null) so the
            // template falls back to the BlueLedger default copy.
            await api.patch(`/orgs/${currentOrg.id}/email-messages`, {
                invoice: invoice.trim() ? invoice.trim() : null,
                estimate: estimate.trim() ? estimate.trim() : null,
                receipt: receipt.trim() ? receipt.trim() : null,
                statement: statement.trim() ? statement.trim() : null,
            });
            await refreshOrgs();
            toast.success("Email messages saved");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    return (
        <section className="rounded-3xl bg-white border border-sand-200 shadow-card p-6 lg:col-span-2">
            <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-secondary-dark" />
                <h3 className="font-heading text-xl text-ocean-700">Outbound email messages</h3>
            </div>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                Customise the short message that appears in the body of each customer-facing email.
                Each message is HTML-escaped — line breaks are preserved. Leave blank to keep
                BlueLedger's default copy.
            </p>
            <div className="mt-5 grid sm:grid-cols-2 gap-5">
                <EmailMsgField
                    label="Invoice email"
                    value={invoice}
                    onChange={setInvoice}
                    defaultText={DEFAULTS.invoice}
                    testId="email-msg-invoice"
                />
                <EmailMsgField
                    label="Estimate email"
                    value={estimate}
                    onChange={setEstimate}
                    defaultText={DEFAULTS.estimate}
                    testId="email-msg-estimate"
                />
                <EmailMsgField
                    label="Payment receipt email"
                    value={receipt}
                    onChange={setReceipt}
                    defaultText={DEFAULTS.receipt}
                    testId="email-msg-receipt"
                />
                <EmailMsgField
                    label="Customer statement email"
                    value={statement}
                    onChange={setStatement}
                    defaultText={DEFAULTS.statement}
                    testId="email-msg-statement"
                />
            </div>
            <Button
                onClick={save}
                disabled={busy}
                className="bg-ocean-700 text-white rounded-xl mt-5"
                data-testid="email-msg-save"
            >
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                Save email messages
            </Button>
        </section>
    );
}


function InvoiceDefaultsSection() {
    const { currentOrg, setCurrentOrg, refreshOrgs } = useAuth();
    const [template, setTemplate] = useState(currentOrg?.invoice_template || "classic");
    const [terms, setTerms] = useState(currentOrg?.default_payment_terms || "");
    const [busy, setBusy] = useState(false);
    const [zoom, setZoom] = useState(null);  // template id to show full-size

    React.useEffect(() => {
        if (currentOrg) {
            setTemplate(currentOrg.invoice_template || "classic");
            setTerms(currentOrg.default_payment_terms || "");
        }
    }, [currentOrg]);

    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.patch(`/orgs/${currentOrg.id}`, {
                invoice_template: template,
                default_payment_terms: terms,
            });
            setCurrentOrg(data);
            await refreshOrgs();
            toast.success("Invoice defaults saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <section className="rounded-3xl bg-white border border-sand-200 shadow-card p-6 lg:col-span-2">
            <div className="flex items-center gap-2">
                <FileIcon className="h-5 w-5 text-secondary-dark" />
                <h3 className="font-heading text-xl text-ocean-700">Invoice defaults</h3>
            </div>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                Pick the template we'll use when emailing invoices to customers and set a default payment
                terms blurb that auto-fills on every new invoice. Each preview shows exactly how the email
                will look in the customer's inbox.
            </p>

            <div className="mt-5">
                <Label className="mb-3 block">Invoice template</Label>
                <div className="grid sm:grid-cols-3 gap-4">
                    {INVOICE_TEMPLATES.map((t) => {
                        const active = template === t.id;
                        return (
                            <div key={t.id} className="flex flex-col">
                                <button
                                    type="button"
                                    onClick={() => setTemplate(t.id)}
                                    data-testid={`template-${t.id}`}
                                    className={`text-left rounded-2xl border overflow-hidden transition-all ${
                                        active
                                            ? "border-secondary ring-2 ring-secondary/30 shadow-md"
                                            : "border-sand-300 hover:border-secondary/40 hover:shadow-sm"
                                    }`}
                                >
                                    <InvoiceEmailPreview template={t.id} orgName={currentOrg?.name || "Your business"} compact />
                                    <div className="px-4 py-3 bg-white">
                                        <div className="flex items-center justify-between">
                                            <div className="font-medium text-ocean-700">{t.name}</div>
                                            {active && (
                                                <div className="inline-flex items-center gap-1 text-xs font-medium text-secondary-dark">
                                                    <Check className="h-3.5 w-3.5" /> Selected
                                                </div>
                                            )}
                                        </div>
                                        <div className="mt-1 text-xs text-muted-foreground leading-snug">{t.tagline}</div>
                                        <div className="mt-2 h-1 rounded-full" style={{ background: t.accent }} />
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setZoom(t.id)}
                                    className="mt-2 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-ocean-700 hover:text-secondary-dark transition-colors"
                                    data-testid={`template-preview-${t.id}-open`}
                                >
                                    <Eye className="h-3.5 w-3.5" /> Preview full size
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="mt-6">
                <Label htmlFor="payment-terms">Default payment terms</Label>
                <Textarea
                    id="payment-terms"
                    value={terms}
                    onChange={(e) => setTerms(e.target.value)}
                    className="mt-1.5"
                    rows={4}
                    placeholder="Payment is due within 30 days of the invoice date. Late payments may incur a 1.5% monthly interest charge. Please make cheques payable to Cayman Reef Holdings."
                    data-testid="payment-terms-input"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                    These terms appear on every outbound invoice email and as the default "Notes" field on new invoices.
                </p>
            </div>

            <Button onClick={save} disabled={busy} className="mt-5 bg-ocean-700 text-white rounded-xl" data-testid="invoice-defaults-save">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                Save invoice defaults
            </Button>

            <Dialog open={!!zoom} onOpenChange={(v) => !v && setZoom(null)}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">
                            {INVOICE_TEMPLATES.find((x) => x.id === zoom)?.name} template preview
                        </DialogTitle>
                    </DialogHeader>
                    {zoom && (
                        <div className="rounded-2xl bg-sand-50/50 border border-sand-200 overflow-auto max-h-[70vh] p-4" data-testid={`template-preview-${zoom}-modal`}>
                            <div className="mx-auto" style={{ width: 720 }}>
                                <InvoiceEmailPreview template={zoom} orgName={currentOrg?.name || "Your business"} />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setZoom(null)}>Close</Button>
                        <Button
                            onClick={() => { setTemplate(zoom); setZoom(null); }}
                            className="bg-ocean-700 text-white"
                            data-testid="template-preview-select"
                        >
                            Use this template
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}

